'use memo';

import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  ConnectCrmAgencyProviderRequestProvider,
  DisconnectCrmAgencyProviderRequestProvider,
  type ConnectCrmAgencyProviderRequest,
  type CrmAgencyConnectionProviderResponse,
  type CrmAgencyConnectionProviderResponseProvider,
  type DisconnectCrmAgencyProviderRequest,
  useConnectCrmAgencyProvider,
  useDisconnectCrmAgencyProvider,
  useGetCrmAgencyConnectionDetails,
  useInvalidateUseGetCrmAgencyConnectionDetails,
} from '@urbanx/agx.crm.client';
import {
  AgxButton,
  AgxLoadingState,
  AgxModal,
  AgxPanel,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_Arrow,
  useToast,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { MenuTitles, SettingsMenu } from 'constants/menu';
import { useSelectedAgency } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import CrmCard from './CrmCard';
import CrmConnectModal from './CrmConnectModal';
import { readCrmErrorMessage } from './helpers';

type CrmProvider = CrmAgencyConnectionProviderResponseProvider;

const connectFailureToastId = 'crm-connect-failure';
const disconnectFailureToastId = 'crm-disconnect-failure';

const toConnectProvider = (provider: CrmProvider) =>
  ConnectCrmAgencyProviderRequestProvider[
    provider as keyof typeof ConnectCrmAgencyProviderRequestProvider
  ];

const toDisconnectProvider = (provider: CrmProvider) =>
  DisconnectCrmAgencyProviderRequestProvider[
    provider as keyof typeof DisconnectCrmAgencyProviderRequestProvider
  ];

const CrmPage = () => {
  const { agencyId } = useParams();
  const { userAccount } = useContext(AuthContext);
  const { pushToast, removeToast } = useToast();
  const queryClient = useQueryClient();
  const invalidateCrmConnectionDetails = useInvalidateUseGetCrmAgencyConnectionDetails();
  const { agency: selectedAgency } = useSelectedAgency();
  const windowSize = useWindowSize();
  const [tertiaryActiveMenu, setTertiaryActiveMenu] = useState<MenuTitles | undefined>(
    MenuTitles.CRM
  );
  const [busyProvider, setBusyProvider] = useState<CrmProvider | null>(null);
  const [disconnectProvider, setDisconnectProvider] = useState<CrmProvider | null>(null);
  const [connectProviderDetails, setConnectProviderDetails] =
    useState<CrmAgencyConnectionProviderResponse | null>(null);

  const {
    data: crmDetails,
    isError: crmDetailsHasError,
    isPending: crmDetailsAreLoading,
  } = useGetCrmAgencyConnectionDetails(agencyId || '', !!agencyId);
  const activeCrmProvider = crmDetails?.activeProvider as CrmProvider | undefined;
  const requestPending = Boolean(busyProvider);
  const getProviderName = (provider?: string, displayName?: string) =>
    displayName ||
    crmDetails?.providers?.find((providerDetails) => providerDetails.provider === provider)
      ?.displayName ||
    provider ||
    'None';

  const pushStickyErrorToast = (message: string, toastId: string) => {
    pushToast(
      {
        type: 'error',
        message,
        action: {
          label: 'Dismiss',
          onClick: () => removeToast(toastId),
        },
      },
      toastId
    );
  };

  const { connectCrmAgencyProvider } = useConnectCrmAgencyProvider({
    onSuccess: async (returnData, inputData) => {
      if (inputData.agencyId) await invalidateCrmConnectionDetails(inputData.agencyId);
      pushToast({
        type: 'success',
        message: `${getProviderName(
          inputData.provider,
          returnData?.providerDetails?.displayName
        )} CRM connected`,
      });
    },
    onError: (error) => {
      pushStickyErrorToast(
        readCrmErrorMessage(error) || 'Unable to connect CRM provider',
        connectFailureToastId
      );
    },
  });
  const { disconnectCrmAgencyProvider } = useDisconnectCrmAgencyProvider({
    onSuccess: async (returnData, inputData) => {
      if (inputData.agencyId) {
        await invalidateCrmConnectionDetails(inputData.agencyId);
        await queryClient.invalidateQueries({
          queryKey: ['getCrmAgentDetails', inputData.agencyId],
        });
      }
      pushToast({
        type: 'success',
        message: `${getProviderName(
          inputData.provider,
          returnData?.providerDetails?.displayName
        )} CRM disconnected`,
      });
    },
    onError: (error) => {
      pushStickyErrorToast(
        readCrmErrorMessage(error) || 'Unable to disconnect CRM provider',
        disconnectFailureToastId
      );
    },
  });

  useEffect(() => {
    if (!crmDetailsHasError) return;

    pushToast({
      type: 'error',
      message: 'Unable to load CRM provider details',
    });
  }, [crmDetailsHasError, pushToast]);

  const connectProvider = async (provider: CrmProvider, fieldValues: Record<string, string>) => {
    const trimmedFieldValues = Object.fromEntries(
      Object.entries(fieldValues).map(([fieldName, value]) => [fieldName, value.trim()])
    );

    if (
      Object.keys(trimmedFieldValues).length === 0 ||
      Object.values(trimmedFieldValues).some((value) => !value)
    )
      return false;

    const request: ConnectCrmAgencyProviderRequest = {
      agencyId: agencyId || '',
      provider: toConnectProvider(provider),
      ...trimmedFieldValues,
    };

    setBusyProvider(provider);
    return connectCrmAgencyProvider(request)
      .then(() => true)
      .catch(() => false)
      .finally(() => setBusyProvider(null));
  };

  const confirmDisconnectProvider = () => {
    if (!disconnectProvider) return;

    const provider = disconnectProvider;
    setDisconnectProvider(null);
    setBusyProvider(provider);
    const request: DisconnectCrmAgencyProviderRequest = {
      agencyId: agencyId || '',
      provider: toDisconnectProvider(provider),
    };

    void disconnectCrmAgencyProvider(request)
      .catch(() => undefined)
      .finally(() => setBusyProvider(null));
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name ?? ''}
      currentPageTitle={MenuTitles.SETTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.SETTINGS}
        tertiaryMenu={SettingsMenu}
        tertiaryActiveMenu={tertiaryActiveMenu}
      >
        {windowSize !== Breakpoints.Desktop && (
          <AgxSpacer
            direction="horizontal"
            fill
            contentJustification="space-between"
            contentAlignment="center"
            className="borderBottomContainer"
          >
            <AgxButton
              id="btn-back"
              leftIcon={
                <Icon_Arrow direction="left" height={22} width={22} color="var(--text-black)" />
              }
              variant="naked"
              size="large"
              text={MenuTitles.CRM}
              onClick={() => setTertiaryActiveMenu(undefined)}
            />
          </AgxSpacer>
        )}
        {crmDetailsAreLoading ? (
          <AgxLoadingState />
        ) : (
          <AgxPanel
            header={
              <AgxTypography id="crm-panel" type="title" size="large" color="var(--text-primary)">
                CRM Provider Management
              </AgxTypography>
            }
          >
            <AgxSpacer
              data-testid="crm-provider-grid"
              fill
              direction="vertical"
              style={{
                alignItems: 'start',
                display: 'grid',
                gap: 'var(--gap-xs, 12px)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 300px))',
              }}
            >
              {crmDetails?.providers?.map((providerDetails) =>
                providerDetails.provider ? (
                  <CrmCard
                    key={providerDetails.provider}
                    activeProvider={activeCrmProvider}
                    busyProvider={busyProvider}
                    providerDetails={providerDetails}
                    requestPending={requestPending}
                    onConnect={setConnectProviderDetails}
                    onDisconnect={setDisconnectProvider}
                  />
                ) : null
              )}
            </AgxSpacer>
          </AgxPanel>
        )}
        {connectProviderDetails && (
          <CrmConnectModal
            providerDetails={connectProviderDetails}
            busy={busyProvider === connectProviderDetails.provider}
            onConnect={connectProvider}
            onClose={() => setConnectProviderDetails(null)}
          />
        )}
        {disconnectProvider && (
          <AgxModal
            title={`Disconnect ${getProviderName(disconnectProvider)} CRM?`}
            type="dialog"
            variant="withDivider"
            actionButtonContainerStyle="withDivider"
            minWidth="500px"
            maxWidth="500px"
            isOpen={Boolean(disconnectProvider)}
            setIsDialogOpen={(open) => {
              if (!open) setDisconnectProvider(null);
            }}
            content={
              <AgxSpacer padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}>
                <AgxTypography id="crm-disconnect-confirmation" type="body_ui" size="medium">
                  Are you sure you want to disconnect the {getProviderName(disconnectProvider)}{' '}
                  provider from this agency? Every Agent in the agency will also be unregistered.
                </AgxTypography>
              </AgxSpacer>
            }
            actionButtons={
              <AgxSpacer direction="horizontal" gap="spacing-md">
                <AgxButton
                  id="crm-cancel-disconnect"
                  text="Cancel"
                  size="medium"
                  variant="hollow"
                  disabled={requestPending}
                  onClick={() => setDisconnectProvider(null)}
                />
                <AgxButton
                  id="crm-confirm-disconnect"
                  text="Yes, disconnect"
                  size="medium"
                  variant="primary"
                  danger
                  disabled={requestPending}
                  onClick={confirmDisconnectProvider}
                />
              </AgxSpacer>
            }
          />
        )}
      </ContentLayout>
    </PageLayout>
  );
};

export default CrmPage;
